import { useMemo, useState } from "react";
import { toast } from "sonner";
import { TestTube, RotateCw, Microscope, Check, X, Droplet } from "lucide-react";

const ACCENT = "#b45309";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* ── Interactive tube rack — match each test to the correct additive tube ───── */
const TUBES = [
  { id: "edta", name: "EDTA", cap: "#a855f7", colourName: "Lavender / purple top", use: "Haematology / CBC", invert: "Invert 8–10×", note: "Potassium-EDTA anticoagulant preserves cell size and morphology for cell counts and blood films. Never use it for biochemistry — it contaminates potassium and calcium results." },
  { id: "serum", name: "Serum / gel (SST)", cap: "#f59e0b", colourName: "Gold / red top", use: "Biochemistry", invert: "Let clot ~20–30 min, then spin", note: "No anticoagulant — the blood clots and the gel separates serum when spun. This is the reference sample for most biochemistry and hormone assays." },
  { id: "fluoride", name: "Fluoride oxalate", cap: "#9ca3af", colourName: "Grey top", use: "Glucose / lactate", invert: "Invert gently to mix", note: "Fluoride inhibits glycolysis so red cells can't consume glucose — it keeps glucose (and lactate) accurate even if the sample sits before being run." },
  { id: "heparin", name: "Lithium heparin", cap: "#22c55e", colourName: "Green top", use: "Plasma biochem / blood gas", invert: "Invert gently 8–10×", note: "Heparin plasma can be spun and run immediately with no clotting wait — useful for rapid in-house biochemistry and blood gases. It distorts cell staining, so it's not used for films." },
  { id: "citrate", name: "Sodium citrate", cap: "#3b82f6", colourName: "Light-blue top", use: "Coagulation", invert: "Fill exactly to the line", note: "Citrate reversibly binds calcium for clotting tests (PT / aPTT). The 9:1 blood-to-citrate ratio is critical — under- or over-filling skews the result." },
];
const SAMPLES = [
  { id: "cbc", label: "CBC", tube: "edta" },
  { id: "biochem", label: "Biochemistry", tube: "serum" },
  { id: "glucose", label: "Glucose", tube: "fluoride" },
  { id: "coag", label: "Coagulation", tube: "citrate" },
];

export function TubeRack() {
  const tubes = useMemo(() => shuffle(TUBES), []);
  const samples = useMemo(() => shuffle(SAMPLES), []);
  const [sel, setSel] = useState(null); // sample id selected
  const [placed, setPlaced] = useState({}); // tubeId -> sampleId
  const [info, setInfo] = useState(null); // last tube id acted on (teaching box)
  const done = Object.keys(placed).length === SAMPLES.length;
  const correct = SAMPLES.filter((s) => placed[s.tube] === s.id).length;
  const samplePlaced = (sid) => Object.values(placed).includes(sid);
  const infoTube = info ? TUBES.find((t) => t.id === info) : null;

  const drop = (tubeId) => {
    if (!sel) return;
    const s = SAMPLES.find((x) => x.id === sel);
    setPlaced((p) => {
      const n = { ...p };
      for (const t of Object.keys(n)) if (n[t] === sel) delete n[t];
      n[tubeId] = sel;
      return n;
    });
    if (s.tube === tubeId) toast.success(`${s.label} → ${TUBES.find((t) => t.id === tubeId).name} ✓`);
    else toast.error(`${TUBES.find((t) => t.id === tubeId).name} isn't ideal for ${s.label}`);
    setInfo(tubeId);
    setSel(null);
  };
  const reset = () => { setPlaced({}); setSel(null); setInfo(null); };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="tube-rack">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <TestTube className="w-3.5 h-3.5" /> Tube rack · match by additive colour
        </span>
        {done && <span className="text-xs font-bold" style={{ color: correct === SAMPLES.length ? "#10b981" : ACCENT }} data-testid="tube-rack-score">{correct}/{SAMPLES.length} correct</span>}
      </div>
      {/* sample chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        {samples.map((s) => {
          const used = samplePlaced(s.id);
          const active = sel === s.id;
          return (
            <button key={s.id} type="button" disabled={used} onClick={() => setSel(active ? null : s.id)} data-testid={`sample-${s.id}`}
              className={`flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-all ${used ? "opacity-30 border-slate-200 bg-slate-100 text-slate-400" : active ? "text-white border-transparent" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
              style={active ? { background: ACCENT } : undefined}>
              <Droplet className="w-3 h-3" /> {s.label}
            </button>
          );
        })}
        <span className="text-[11px] text-slate-400 self-center">tap a sample, then tap its tube</span>
      </div>
      {/* rack */}
      <div className="grid grid-cols-5 gap-2">
        {tubes.map((t) => {
          const has = placed[t.id];
          const s = has ? SAMPLES.find((x) => x.id === has) : null;
          const ok = s && s.tube === t.id;
          return (
            <button key={t.id} type="button" onClick={() => drop(t.id)} data-testid={`tube-${t.id}`}
              className={`flex flex-col items-center rounded-md border-2 p-1.5 transition-all ${has ? (ok ? "border-emerald-400 bg-emerald-50" : "border-rose-400 bg-rose-50") : sel ? "border-dashed border-amber-400 bg-amber-50/40" : "border-slate-200 bg-slate-50"}`}>
              <svg viewBox="0 0 20 50" className="w-5 h-12">
                <rect x="6" y="1" width="8" height="6" rx="1.5" fill={t.cap} />
                <rect x="5" y="7" width="10" height="40" rx="3" fill="#fff" stroke="#cbd5e1" strokeWidth="1.2" />
                {has && <rect x="5" y="27" width="10" height="20" rx="3" fill="#b91c1c" opacity="0.55" />}
              </svg>
              <span className="text-[9px] font-semibold text-slate-700 leading-tight text-center mt-0.5">{t.name}</span>
              <span className="text-[8px] text-slate-400 leading-tight text-center">{t.use}</span>
              {has && (ok ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <X className="w-3.5 h-3.5 text-rose-500" />)}
            </button>
          );
        })}
      </div>
      {/* post-selection teaching box */}
      {infoTube && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="tube-info">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0" style={{ background: infoTube.cap }} data-testid="tube-info-swatch" />
            <span className="text-[12px] font-semibold text-slate-900">{infoTube.name}</span>
            <span className="text-[11px] text-slate-500">· {infoTube.colourName}</span>
          </div>
          <div className="text-[11px] text-slate-600 leading-snug">
            <span className="font-semibold text-[#b45309]">Used for:</span> {infoTube.use}
            <span className="mx-1.5 text-slate-300">|</span>
            <span className="font-semibold text-[#b45309]">Handling:</span> {infoTube.invert}
          </div>
          <div className="text-[11px] text-slate-600 leading-snug mt-1">{infoTube.note}</div>
        </div>
      )}
      {Object.keys(placed).length > 0 && (
        <button type="button" onClick={reset} data-testid="tube-rack-reset" className="mt-2 text-[11px] font-semibold text-slate-500 hover:text-[#b45309]">Reset rack</button>
      )}
    </div>
  );
}

/* ── Centrifuge animation ──────────────────────────────────────────────────── */
export function Centrifuge() {
  const [spinning, setSpinning] = useState(false);
  const [spun, setSpun] = useState(false);
  const spin = () => {
    if (spinning || spun) return;
    setSpinning(true);
    setTimeout(() => { setSpinning(false); setSpun(true); }, 2200);
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-4" data-testid="centrifuge">
      <div className={`relative w-20 h-20 ${spinning ? "animate-spin" : ""}`} style={{ animationDuration: "0.4s" }}>
        <svg viewBox="0 0 80 80" className="w-full h-full">
          <circle cx="40" cy="40" r="38" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="2" />
          <circle cx="40" cy="40" r="6" fill="#64748b" />
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const rad = (deg * Math.PI) / 180;
            const x = 40 + Math.cos(rad) * 24;
            const y = 40 + Math.sin(rad) * 24;
            return (
              <g key={deg} transform={`rotate(${deg + 90} ${x} ${y})`}>
                <rect x={x - 3.5} y={y - 9} width="7" height="18" rx="2" fill="#fff" stroke="#94a3b8" strokeWidth="1" />
                {spun ? (
                  <>
                    <rect x={x - 3.5} y={y - 9} width="7" height="9" rx="2" fill="#fde68a" />
                    <rect x={x - 3.5} y={y} width="7" height="9" rx="2" fill="#b91c1c" opacity="0.6" />
                  </>
                ) : (
                  <rect x={x - 3.5} y={y - 9} width="7" height="18" rx="2" fill="#b91c1c" opacity="0.5" />
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Centrifuge</div>
        <p className="text-[12px] text-slate-600 leading-snug">{spun ? "Spun — plasma/serum (top), buffy coat, packed cells (bottom). Separate the serum promptly." : "Spin the samples to separate serum/plasma from cells before biochemistry."}</p>
        {!spun && (
          <button type="button" onClick={spin} disabled={spinning} data-testid="centrifuge-spin-btn" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-md px-3 py-1.5 disabled:opacity-50" style={{ background: ACCENT }}>
            <RotateCw className="w-3.5 h-3.5" /> {spinning ? "Spinning…" : "Spin (3000 rpm)"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Pre-analytical artefact gallery ───────────────────────────────────────── */
const ARTEFACTS = [
  { id: "normal", name: "Normal", color: "#fde68a", effect: "Clear, straw-coloured serum — no interference." },
  { id: "haemolysis", name: "Haemolysed", color: "#dc2626", effect: "Falsely ↑ potassium, phosphate & some enzymes; interferes with bilirubin. Caused by rough handling/shaking." },
  { id: "lipaemia", name: "Lipaemic", color: "#fef3c7", milky: true, effect: "Turbid/milky — interferes with many photometric assays. Fast the patient before sampling." },
  { id: "icterus", name: "Icteric", color: "#ca8a04", effect: "Deep yellow from bilirubin — a true finding (haemolysis/liver) but can interfere with some assays." },
];
export function ArtefactGallery() {
  const [pick, setPick] = useState("normal");
  const a = ARTEFACTS.find((x) => x.id === pick);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="artefact-gallery">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Pre-analytical artefact gallery</div>
      <div className="grid grid-cols-4 gap-2">
        {ARTEFACTS.map((x) => (
          <button key={x.id} type="button" onClick={() => setPick(x.id)} data-testid={`artefact-${x.id}`}
            className={`flex flex-col items-center rounded-md border-2 p-1.5 transition-all ${pick === x.id ? "border-[#b45309] bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
            <svg viewBox="0 0 24 54" className="w-6 h-14">
              <rect x="7" y="1" width="10" height="6" rx="1.5" fill="#64748b" />
              <rect x="6" y="7" width="12" height="44" rx="3" fill="#fff" stroke="#cbd5e1" strokeWidth="1.2" />
              {/* serum (top) */}
              <rect x="6" y="9" width="12" height="20" rx="2" fill={x.color} opacity={x.milky ? 0.9 : 0.8} />
              {/* cells (bottom) */}
              <rect x="6" y="33" width="12" height="18" rx="2" fill="#b91c1c" opacity="0.55" />
            </svg>
            <span className="text-[9px] font-semibold text-slate-700 mt-0.5">{x.name}</span>
          </button>
        ))}
      </div>
      <div className="mt-2 text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="artefact-effect">
        <span className="font-semibold text-slate-900">{a.name}:</span> {a.effect}
      </div>
    </div>
  );
}

/* ── Blood-film microscope — click the requested cell ──────────────────────── */
const FILM_TARGETS = [
  { id: "neutrophil", label: "neutrophil", color: "#c4b5fd", lobed: true },
  { id: "lymphocyte", label: "lymphocyte", color: "#a5b4fc", lobed: false },
  { id: "eosinophil", label: "eosinophil", color: "#fca5a5", lobed: true },
];
function makeField() {
  const cells = [];
  // RBCs (biconcave discs) scattered
  for (let i = 0; i < 16; i += 1) cells.push({ type: "rbc", x: 10 + Math.random() * 80, y: 10 + Math.random() * 80, r: 5 });
  // one of each WBC
  FILM_TARGETS.forEach((t, i) => cells.push({ type: t.id, x: 22 + i * 26 + Math.random() * 8, y: 30 + Math.random() * 36, r: 8, color: t.color, lobed: t.lobed }));
  return cells;
}
export function BloodFilmScope() {
  const [cells] = useState(makeField);
  const [target] = useState(() => FILM_TARGETS[Math.floor(Math.random() * FILM_TARGETS.length)]);
  const [clicked, setClicked] = useState(null);
  const correct = clicked === target.id;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="blood-film">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <Microscope className="w-3.5 h-3.5" /> Blood film · ×100 oil
        </span>
        <span className="text-xs font-semibold text-slate-700" data-testid="film-target">Find the <span style={{ color: ACCENT }}>{target.label}</span></span>
      </div>
      <div className="relative rounded-full overflow-hidden border-4 border-slate-800 mx-auto" style={{ width: 260, height: 260, background: "radial-gradient(circle,#f5d0e8 0%,#e7a8d0 75%,#c97fb4 100%)" }} data-testid="film-field">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {cells.map((c, i) => {
            if (c.type === "rbc")
              return <circle key={i} cx={c.x} cy={c.y} r={c.r} fill="#ec7fb0" stroke="#d65f97" strokeWidth="0.8" />;
            const sel = clicked && cells.find((x, idx) => idx === i && x.type === clicked);
            const isTarget = c.type === target.id;
            return (
              <g key={i} onClick={() => !clicked && setClicked(c.type)} style={{ cursor: clicked ? "default" : "pointer" }} data-testid={`film-cell-${c.type}`}>
                <circle cx={c.x} cy={c.y} r={c.r} fill="#dbeafe" stroke="#60a5fa" strokeWidth="0.8" />
                <circle cx={c.x} cy={c.y} r={c.r - 2} fill={c.color} />
                {clicked && isTarget && <circle cx={c.x} cy={c.y} r={c.r + 2} fill="none" stroke="#10b981" strokeWidth="1.6" />}
                {clicked === c.type && !isTarget && <circle cx={c.x} cy={c.y} r={c.r + 2} fill="none" stroke="#ef4444" strokeWidth="1.6" />}
              </g>
            );
          })}
        </svg>
      </div>
      {clicked ? (
        <div className={`mt-2 text-[12px] font-semibold rounded-md px-3 py-2 ${correct ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-600 border border-rose-200"}`} data-testid="film-result">
          {correct ? `Correct — that's the ${target.label} (the larger nucleated cell among the smaller pink RBCs).` : `Not quite — the ${target.label} is the highlighted cell. White cells are larger and nucleated.`}
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 mt-2 text-center">Click the nucleated white cell that matches the prompt.</p>
      )}
    </div>
  );
}
